import React from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';

import {labels} from '../../../util/utils';
import Utility from '../../../util/utilityfunctions';

import OutputText from '@ey/canvascoreservices/OutputText';

import Legend from './Legend';

const GroupedStackedBarChart = ({categories, title, series, onGraphicClick}) => {
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);

	const optionsBar = {
		chart: {
			type: 'column',
			backgroundColor: 'transparent',
			height: 250,
			width: 395
		},
		credits: {enabled: false},
		title: {text: ''},
		subtitle: {text: '', verticalAlign: 'middle', fontSize: '1.2rem'},
		xAxis: {
			title: {text: labels.readinessLegendTitle},
			categories: categories,
			scrollbar: {enabled: false},
			visible: true,
			plotLines: [
				{
					color: '#e1e1e6',
					width: 2,
					value: 2.5,
					dashStyle: 'ShortDash'
				}
			],
			labels: {
				style: {width: '150px', textAlign: 'center'},
				autoRotationLimit: 20,
				useHTML: true,
				formatter(e) {
					let categoryLabelParts = e.value.split('{break}');
					return `
						<div style="display: flex; flex-direction: column;">
							<span class="motif-body2-default-light motif-body3-default-light">${categoryLabelParts[0]}</span>
							<span class="motif-body2-default-regular motif-body3-default-regular">${categoryLabelParts[1]}</span>
						</div>
					`;
				}
			},
			className: 'motif-body3-default-regular'
		},
		yAxis: {
			allowDecimals: false,
			min: 0,
			title: {text: title},
			visible: true,
			labels: {
				useHTML: true,
				formatter: function() {
					return `<span class="motif-body2-default-regular">${Utility.getFormattedNumber(
						numberFormat,
						this.value
					)}</span>`;
				}
			},
			className: 'motif-body3-default-regular'
		},
		plotOptions: {
			bar: {maxPointWidth: 20}, // column bar width
			series: {
				borderColor: 'var(--neutrals-25)',
				borderWidth: 2,
				cursor: 'pointer',
				pointWidth: 30,
				point: {
					events: {
						mouseOver: function() {
							const gray = '#eaeaf2';
							const newGray = '#fafafc';

							this.options.oldColor = this.color;

							if (gray == this.color?.toLowerCase()) {
								this.graphic.attr('fill', newGray);
							}
						},
						mouseOut: function() {
							this.graphic.attr('fill', this.options.oldColor);
						},
						click: (e) => {
							onGraphicClick && onGraphicClick(e);
						},
						point: {
							events: {
								click: (e) => {
									onGraphicClick && onGraphicClick(e);
								}
							}
						}
					}
				}
			},
			column: {
				stacking: 'normal',
				dataLabels: {
					enabled: false
				}
			}
		},
		tooltip: {
			useHTML: true,
			enabled: true,
			outside: true,
			followPointer: true,
			useHTML: true,
			formatter() {
				let countEngagement = this.point.y;
				let label =
					countEngagement === 1
						? labels.engagementCompletedBasedOnTaskCompletionReporting
						: labels.engagementsCompletedBasedOnTaskCompletionReporting;
				return `
					<div class="bar-tooltip" style="width: 20rem; white-space: initial">
						${label
							.replace('{0}', Utility.getFormattedNumber(numberFormat, countEngagement))
							.replace('{1}', this.point['series'].name)}
					</div>
				`;
			}
		},
		legend: {
			enabled: false
		},
		series: series
	};

	const renderLegend = ({name}) => {
		return (
			<>
				<OutputText body2 light className="grey">
					{name}
				</OutputText>
			</>
		);
	};

	return (
		<StyledGroupedStackedBarChart>
			<HighchartsReact highcharts={Highcharts} options={optionsBar} />
			<StyledLegend>
				<Legend
					items={series.slice(0, 4)}
					renderItem={renderLegend}
					title={labels.comingDue}
					horizontal
					className="comming-due"
				/>
				<Legend
					items={series.slice(4, 8)}
					renderItem={renderLegend}
					title={labels.overdueLabel}
					horizontal
					className="over-due"
				/>
			</StyledLegend>
		</StyledGroupedStackedBarChart>
	);
};

GroupedStackedBarChart.defaultProps = {
	onGraphicClick: (e) => {}
};

GroupedStackedBarChart.propTypes = {
	categories: PropTypes.arrayOf(PropTypes.any),
	title: PropTypes.string,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			data: PropTypes.arrayOf(PropTypes.number),
			color: PropTypes.string
		})
	),
	onGraphicClick: PropTypes.func
};

export default GroupedStackedBarChart;

const StyledLegend = styled.div`
	display: grid;
	grid-template-columns: 1fr var(--px-320) 1fr;
	grid-template-rows: auto auto;
	grid-template-areas:
		'. commingDue .'
		'. overDue .';

	.legend {
		justify-content: space-between;
		&.comming-due {
			grid-area: commingDue;
		}
		&.over-due {
			grid-area: overDue;
		}
	}
`;

const StyledGroupedStackedBarChart = styled.div`
	align-self: center;
`;
